import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";

const BASE = "https://gufgaff.com/api";
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [token,       setToken]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [authGate,    setAuthGate]    = useState({ visible: false, action: "" });

  const verifySession = useCallback(async (savedToken) => {
    try {
      const res  = await fetch(`${BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${savedToken}` },
      });
      const data = await res.json();
      if (data.user) {
        setCurrentUser(data.user);
        setToken(savedToken);
      } else {
        // Token expired or invalid — clear session
        localStorage.removeItem("guffgaff_token");
        localStorage.removeItem("guffgaff_user");
      }
    } catch {
      localStorage.removeItem("guffgaff_token");
      localStorage.removeItem("guffgaff_user");
    } finally {
      setLoading(false);
    }
  }, []);

  // Wrap authenticated fetch calls — auto-logout on 401/422 (expired token)
  const authFetch = useCallback(async (url, options = {}) => {
    const res = await fetch(url, options);
    if (res.status === 401 || res.status === 422) {
      const data = await res.json().catch(() => ({}));
      // Only auto-logout for token-related errors, not general permission errors
      if (data.msg && (data.msg.includes("expired") || data.msg.includes("Invalid") || data.msg.includes("token"))) {
        localStorage.removeItem("guffgaff_token");
        localStorage.removeItem("guffgaff_user");
        setToken(null);
        setCurrentUser(null);
        return { _authError: true, error: "Session expired — please sign in again." };
      }
    }
    return res;
  }, []);

  useEffect(() => {
    const savedToken = localStorage.getItem("guffgaff_token");
    if (savedToken) {
      verifySession(savedToken);
    } else {
      setLoading(false);
    }
  }, [verifySession]);

  function login(tokenValue, user) {
    localStorage.setItem("guffgaff_token", tokenValue);
    localStorage.setItem("guffgaff_user", JSON.stringify(user));
    setToken(tokenValue);
    setCurrentUser(user);
  }

  async function completeRegistration(firebaseUid, username, password, email, phone, verifiedToken) {
    const body = { username, password };

    // Phone flow uses firebase_uid; email OTP flow uses verified_token
    if (verifiedToken) {
      body.verified_token = verifiedToken;
    } else {
      body.firebase_uid = firebaseUid;
    }
    if (email) body.email = email;
    if (phone) body.phone = phone;

    const res = await fetch(`${BASE}/auth/complete-registration`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then(r => r.json());

    if (res.token) {
      login(res.token, res.user);
      return res;
    }
    throw new Error(res.error || "Failed to create account");
  }

  async function logout() {
    try { await signOut(auth); } catch { /* ignore */ }
    localStorage.removeItem("guffgaff_token");
    localStorage.removeItem("guffgaff_user");
    setToken(null);
    setCurrentUser(null);
  }

  function showAuthGate(action = "continue") {
    setAuthGate({ visible: true, action });
  }

  function closeAuthGate() {
    setAuthGate({ visible: false, action: "" });
  }

  return (
    <AuthContext.Provider value={{
      currentUser,
      token,
      loading,
      isLoggedIn: !!currentUser,
      login,
      logout,
      completeRegistration,
      authGate,
      showAuthGate,
      closeAuthGate,
      authFetch,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
